import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService, AuthError } from '@/services/authService';
import { User } from '@/types/User';

import { logger } from '@/utils/logger';
import { identify as posthogIdentify, reset as posthogReset } from '@/observability/posthog';
interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: AuthError }>;
  signOut: () => Promise<{ error?: AuthError }>;
  refreshUser: () => Promise<void>;
  clearSession: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Forward auth state to PostHog (no-op if SDK not initialised)
  useEffect(() => {
    if (user) {
      posthogIdentify(user.id || user.email, {
        email: user.email,
        roles: user.roles,
        status: user.status,
      });
    }
  }, [user]);

  // Initialize authentication state (cookie-based)
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const sessionResult = await authService.checkSession();
        if (mounted) {
          if (sessionResult.user) {
            setUser(sessionResult.user);
          } else {
            setUser(null);
          }
          setLoading(false);
        }
      } catch (error) {
        logger.error('Auth initialization error:', error);
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();
    return () => {
      mounted = false;
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const result = await authService.signIn(email, password);
      if (result.user) {
        setUser(result.user);
        return { error: undefined };
      } else {
        setUser(null);
        return { error: result.error };
      }
    } catch (error) {
      logger.error('Sign in error:', error);
      setUser(null);
      return {
        error: {
          message: 'An unexpected error occurred during login',
          type: 'unexpected_error',
        } as AuthError,
      };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      const result = await authService.signOut();
      setUser(null);
      posthogReset();
      return { error: result.error };
    } catch (error) {
      logger.error('Sign out error:', error);
      setUser(null);
      return {
        error: {
          message: 'An error occurred during logout',
          type: 'logout_error',
        } as AuthError,
      };
    } finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    setUser(null);
    setLoading(false);
    posthogReset();
  };

  const refreshUser = async () => {
    try {
      const result = await authService.getCurrentUser(); // force refresh
      if (result.user) {
        setUser(result.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      logger.error('Refresh user error:', error);
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    refreshUser,
    clearSession,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
